import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// ============================================================================
// AUTO ACCEPT FAILURE MODAL — Oto onay/yazdırma başarısız uyarısı
// ============================================================================
// Oto kabul akışı (ve manuel "Onayla ve Yazdır") hataları eskiden yalnızca
// console'a yazılıyordu: NewOrderModal geri sayımı bitiriyor, kapanıyor ve
// kimse siparişin onaylanmadığını/basılmadığını fark etmiyordu. Bu modal o
// sessiz hatayı yeni sipariş uyarısı kadar görünür kılar (ayrı alarm sesiyle).
// ============================================================================

export interface AutoAcceptFailure {
  /** Kuyruk anahtarı — aynı sipariş için ikinci uyarı üretmemek üzere. */
  key: string;
  /** "Trendyol Yemek", "Getir Yemek", "QR Menü" vb. */
  platformLabel: string;
  /** Sipariş numarası / kısa kod. */
  orderNo: string;
  customerName?: string;
  totalAmount?: number;
  /** true: oto geri sayımdan, false: kasiyerin bastığı butondan. */
  automatic: boolean;
  /** Platform onay adımı başarısız mı? (fişten bağımsız, daha kritik) */
  acceptFailed: boolean;
  /** Adım adım hata mesajları — kullanıcıya olduğu gibi gösterilir. */
  problems: string[];
  occurredAt: number;
}

export interface AutoAcceptFailureModalProps {
  open: boolean;
  failure: AutoAcceptFailure | null;
  /** Bu uyarıdan sonra kuyrukta bekleyen hata sayısı. */
  remainingCount?: number;
  onAcknowledge: () => void;
}

const AutoAcceptFailureModal: React.FC<AutoAcceptFailureModalProps> = ({
  open,
  failure,
  remainingCount = 0,
  onAcknowledge,
}) => {
  const { t } = useTranslation('salesSystem');

  if (!failure) return null;

  return (
    <Dialog
      open={open}
      // Yanlışlıkla dışarı tıklayıp kapatmak yok: uyarı bilinçli onaylanmalı.
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          border: '3px solid #d32f2f',
          animation: 'shake 0.5s ease-in-out',
          '@keyframes shake': {
            '0%, 100%': { transform: 'translateX(0)' },
            '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
            '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
          },
        },
      }}
    >
      <Box sx={{ bgcolor: '#d32f2f', color: 'white', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '12px',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'pulse 1s infinite',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.1)' },
              '100%': { transform: 'scale(1)' },
            },
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 34, color: 'white' }} />
        </Box>
        <Box flex={1}>
          <Typography variant="h6" fontWeight="bold">
            {t('orderManagement.autoAcceptFailureModal.title')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {failure.automatic
              ? t('orderManagement.autoAcceptFailureModal.subtitleAuto')
              : t('orderManagement.autoAcceptFailureModal.subtitleManual')}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('orderManagement.autoAcceptFailureModal.orderLabel')}
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            {failure.platformLabel} · #{failure.orderNo}
          </Typography>
        </Box>

        {failure.customerName && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('orderManagement.autoAcceptFailureModal.customerLabel')}
            </Typography>
            <Typography variant="body1" fontWeight="medium">{failure.customerName}</Typography>
          </Box>
        )}

        {typeof failure.totalAmount === 'number' && failure.totalAmount > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('orderManagement.autoAcceptFailureModal.totalLabel')}
            </Typography>
            <Typography variant="body1" fontWeight="bold">{failure.totalAmount.toFixed(2)} ₺</Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {failure.acceptFailed && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#ffebee', borderRadius: 1, display: 'flex', gap: 1 }}>
            <ReportProblemIcon sx={{ color: '#d32f2f' }} />
            <Typography variant="body2" color="error.dark" fontWeight="bold">
              {t('orderManagement.autoAcceptFailureModal.acceptFailedWarning')}
            </Typography>
          </Box>
        )}

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('orderManagement.autoAcceptFailureModal.problemsTitle')}
        </Typography>
        <Box sx={{ maxHeight: 220, overflowY: 'auto', mb: 2 }}>
          {failure.problems.map((problem, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 18, color: '#d32f2f', mt: '2px' }} />
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{problem}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.dark" fontWeight="bold" gutterBottom>
            {t('orderManagement.autoAcceptFailureModal.guidanceTitle')}
          </Typography>
          <Typography variant="body2" color="warning.dark">
            • {t('orderManagement.autoAcceptFailureModal.guidanceAcceptAndPrint')}
          </Typography>
          <Typography variant="body2" color="warning.dark">
            • {t('orderManagement.autoAcceptFailureModal.guidancePrinter')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
        {remainingCount > 0 && (
          <Typography variant="body2" color="error.main" fontWeight="bold">
            {t('orderManagement.autoAcceptFailureModal.remaining', { pendingCount: remainingCount })}
          </Typography>
        )}
        <Button
          variant="contained"
          color="error"
          size="large"
          fullWidth
          onClick={onAcknowledge}
          startIcon={<CheckCircleOutlineIcon />}
        >
          {t('orderManagement.autoAcceptFailureModal.ack')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoAcceptFailureModal;
